import React, { useState } from 'react';
import { Staff, ScheduleItem, Appointment, Task, Doctor, Room } from './SchedulerTypes';
import TimeSlotCell from './TimeSlotCell';
import StickyTableWrapper from './StickyTableWrapper';
import MultiItemSelectionModal from './MultiItemSelectionModal';

interface StaffScheduleTableProps {
  timeSlots: string[];
  scheduleItems: ScheduleItem[];
  staff: Staff[];
  doctors?: Doctor[];
  rooms?: Room[];
  appointments?: Appointment[];
  tasks?: Task[];
  getAppointmentForScheduleItem?: (scheduleItemId: string) => Appointment | undefined;
  getTaskForScheduleItem?: (scheduleItemId: string) => Task | undefined;
  onCellClick: (time: string, personId: string, isDoctor?: boolean) => void;
  onEditItem: (item: ScheduleItem) => void;
  onBookAppointment?: (schedule: ScheduleItem) => void;
  onViewAppointment?: (appointment: Appointment) => void;
  onAssignTask?: (schedule: ScheduleItem) => void;
  onViewTask?: (task: Task) => void;
  onAddScheduleItem?: (item: Omit<ScheduleItem, 'id'>) => void;
  onDeleteScheduleItem?: (id: string) => void;
  currentDate?: string;
}

const StaffScheduleTable = ({
  timeSlots,
  scheduleItems,
  staff,
  doctors = [],
  rooms = [],
  appointments = [],
  tasks = [],
  getAppointmentForScheduleItem = () => undefined,
  getTaskForScheduleItem = () => undefined,
  onCellClick,
  onEditItem,
  onBookAppointment = () => {},
  onViewAppointment = () => {},
  onAssignTask = () => {},
  onViewTask = () => {},
  onAddScheduleItem,
  onDeleteScheduleItem,
  currentDate,
}: StaffScheduleTableProps) => {
  // Sort staff by role group, then by priority (0=highest), then by name
  const sortStaffByRoleAndPriority = (a: Staff, b: Staff) => {
    const getRolePriority = (role: string) => {
      const roleLower = role.toLowerCase();
      if (roleLower.includes('dental assistant')) return 1;
      if (roleLower.includes('dental hygienist')) return 2;
      if (roleLower.includes('dental therapist')) return 3;
      if (roleLower.includes('receptionist')) return 4;
      return 5; // Other roles
    };

    // Primary sort by role group
    const roleComparison = getRolePriority(a.role) - getRolePriority(b.role);
    if (roleComparison !== 0) return roleComparison;
    
    // Secondary sort by priority within same role (0 is highest priority)
    const priorityA = a.priority || 0;
    const priorityB = b.priority || 0;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Tertiary sort by name within same role and priority
    return a.name.localeCompare(b.name);
  };

  const sortedStaff = [...staff].sort(sortStaffByRoleAndPriority);

  // Multi-item selection modal state
  const [multiSelectItems, setMultiSelectItems] = useState<ScheduleItem[]>([]);
  const [multiSelectTime, setMultiSelectTime] = useState<string>('');
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);

  const handleShowMultiItemSelection = (items: ScheduleItem[], time: string) => {
    setMultiSelectItems(items);
    setMultiSelectTime(time);
    setIsMultiSelectOpen(true);
  };

  const handleSelectItem = (item: ScheduleItem) => {
    onEditItem(item);
  };

  const renderStaffHeader = (person: Staff) => {
    return (
      <th key={person.id} className="h-12 px-4 text-center align-middle font-medium text-muted-foreground text-xs">
        <div className="text-sm font-medium">{person.short_name || person.name}</div>
        <div className="text-xs text-muted-foreground">{person.role}</div>
      </th>
    );
  };

  const renderStaffCell = (time: string, staffId: string) => {
    return (
      <TimeSlotCell
        key={`${time}-${staffId}`}
        time={time}
        personId={staffId}
        isDoctor={false}
        scheduleItems={scheduleItems || []}
        appointments={appointments}
        tasks={tasks}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        getAppointmentForScheduleItem={getAppointmentForScheduleItem}
        getTaskForScheduleItem={getTaskForScheduleItem}
        onCellClick={onCellClick}
        onEditItem={onEditItem}
        onBookAppointment={onBookAppointment}
        onViewAppointment={onViewAppointment}
        onAssignTask={onAssignTask}
        onViewTask={onViewTask}
        onAddScheduleItem={onAddScheduleItem}
        onDeleteScheduleItem={onDeleteScheduleItem}
        currentDate={currentDate}
        onShowMultiItemSelection={handleShowMultiItemSelection}
      />
    );
  };

  return (
    <>
    <StickyTableWrapper>
      <thead className="[&_tr]:border-b">
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
            <th className="h-12 px-4 text-center align-middle font-medium text-muted-foreground w-16">TIME</th>
            {/* Staff - sorted by role and priority */}
            {sortedStaff.map(staffMember => renderStaffHeader(staffMember))}
          </tr>
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {timeSlots.map(time => (
            <tr key={time} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
              <td className="p-4 align-middle font-mono text-xs text-center bg-muted/30">
                {time}
              </td>
              {/* Staff - sorted by role and priority */}
              {sortedStaff.map(staffMember => renderStaffCell(time, staffMember.id))}
            </tr>
          ))}
      </tbody>
    </StickyTableWrapper>

    {/* Multi-item selection modal */}
    <MultiItemSelectionModal
      isOpen={isMultiSelectOpen}
      onClose={() => setIsMultiSelectOpen(false)}
      items={multiSelectItems}
      onSelectItem={handleSelectItem}
      time={multiSelectTime}
      doctors={doctors}
      staff={staff}
      rooms={rooms}
    />
    </>
  );
};

export default StaffScheduleTable;
